import {
	ClickAwayListener,
	Dialog,
	DialogContent,
	IconButton,
	Popper,
	Typography,
	styled,
} from '@mui/material'
import { Box } from '@mui/system'
import './customTooltip.scss'
import { PopoverContainer } from './PopoverContainer'
import {
	LandingPageProjectInfo,
	ProjectAttributeKey,
	ProjectSuspensionArea,
} from '@models/projects.model'
import CloseIcon from '@mui/icons-material/Close'
import { isBrowser } from 'react-device-detect'
import { useState } from 'react'
import Countdown from 'react-countdown'
import { isJSON } from '@utils/json_helper'

import DOMPurify from 'dompurify'

interface CustomToolTipProps {
	markerElem: LandingPageProjectInfo
	open: boolean
	onClose: () => void
	anchorEl: HTMLDivElement | null
	handleClickMarker: any
	id?: string
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
	'& .MuiDialogContent-root': {
		padding: theme.spacing(2),
	},
	'& .MuiDialogActions-root': {
		padding: theme.spacing(1),
	},
}))

const AnnouncementArea = ({
	hideClose,
	allowCloseAfterSeconds,
	content,
	handleCloseAnnouncement,
}: ProjectSuspensionArea & {
	handleCloseAnnouncement: () => void
}) => {
	const htmlSanatized = DOMPurify.sanitize(content)

	return (
		<Box
			sx={{
				overflow: 'hidden',
			}}
		>
			{!hideClose && (
				<CountdownCloseBtn
					onClose={handleCloseAnnouncement}
					allowCloseAfterSeconds={allowCloseAfterSeconds || 10}
				/>
			)}

			<div dangerouslySetInnerHTML={{ __html: htmlSanatized }} />
		</Box>
	)
}

const CountdownCloseBtn = ({
	onClose,
	allowCloseAfterSeconds,
}: {
	onClose: () => void
	allowCloseAfterSeconds: number
}) => {
	return (
		<Countdown
			date={Date.now() + allowCloseAfterSeconds * 1000}
			renderer={({ hours, minutes, seconds, completed }) => {
				if (completed) {
					// Render a completed state
					return (
						<IconButton
							aria-label="close"
							onClick={onClose}
							sx={{
								position: 'absolute',
								right: 8,
								top: 8,
								backgroundColor: '#f0f0f0',
								height: '1rem',
								width: '1rem',
								fontSize: 'initial',
								padding: '16px',
								'&:hover': {
									backgroundColor: '#f0f0f0',
								},
							}}
						>
							<CloseIcon />
						</IconButton>
					)
				} else {
					// Render a countdown
					return (
						<IconButton
							aria-label="close"
							sx={{
								position: 'absolute',
								right: 8,
								top: 8,
								backgroundColor: '#f0f0f0',
								height: '1rem',
								width: '1rem',
								fontSize: 'initial',
								padding: '16px',
							}}
							disableRipple
						>
							<span>{seconds}</span>
						</IconButton>
					)
				}
			}}
		></Countdown>
	)
}

export const CustomToolTip = ({
	id,
	markerElem,
	open,
	onClose,
	anchorEl,
	handleClickMarker,
}: CustomToolTipProps) => {
	const attributes = markerElem.attributes
	const projectSuspensionAttr = attributes?.find(
		(x) => x.key === ProjectAttributeKey.ServiceSuspension,
	)

	const projectSuspensionObj: ProjectSuspensionArea | undefined =
		projectSuspensionAttr &&
		isJSON(projectSuspensionAttr?.value || '') &&
		JSON.parse(projectSuspensionAttr.value)

	const [showAnnouncement, setShowAnnouncement] = useState<boolean>(
		projectSuspensionObj !== undefined && projectSuspensionObj.content !== '',
	)
	const handleCloseAnnouncement = () => {
		setShowAnnouncement(false)
	}

	console.debug('projectSuspensionObj', projectSuspensionObj)
	const handleClose = () => {
		setShowAnnouncement(projectSuspensionObj !== undefined && projectSuspensionObj.content !== '')
		if (onClose) onClose()
	}

	return (
		<Box id={id}>
			<Typography
				onClick={handleClickMarker}
				sx={{
					width: '250px',
					wordWrap: 'break-word',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					whiteSpace: 'normal',
					textAlign: 'center',
				}}
				variant="body2"
			>
				{markerElem['display_name']}
			</Typography>

			<ClickAwayListener onClickAway={handleClose}>
				{isBrowser ? (
					<Popper
						id={'PopoverContainer'}
						open={open}
						anchorEl={anchorEl}
						placement="left"
						sx={{
							backgroundColor: '#7b7b7bbf',
							borderRadius: '10px',
							maxWidth: '600px',
							overflow: 'hidden',
							overflowY: 'scroll',
							'::-webkit-scrollbar': {
								display: 'none',
							},
						}}
					>
						{showAnnouncement && projectSuspensionObj ? (
							<AnnouncementArea
								{...projectSuspensionObj}
								handleCloseAnnouncement={handleCloseAnnouncement}
							/>
						) : (
							<PopoverContainer markerElem={markerElem} />
						)}
					</Popper>
				) : (
					<BootstrapDialog
						onClose={handleClose}
						aria-labelledby="customized-dialog-title"
						open={open}
						sx={{
							'& .MuiPaper-root ': {
								backgroundColor: '#7b7b7bbf',
								borderRadius: '10px',
							},
							overflow: 'scroll',
							'::-webkit-scrollbar': {
								display: 'none',
							},
						}}
						PaperProps={{
							sx: {
								m: '8px',
							},
						}}
					>
						<DialogContent>
							{showAnnouncement && projectSuspensionObj ? (
								<AnnouncementArea
									{...projectSuspensionObj}
									handleCloseAnnouncement={handleCloseAnnouncement}
								/>
							) : (
								[
									<IconButton
										key={'dialog_close'}
										aria-label="close"
										onClick={handleClose}
										sx={{
											position: 'absolute',
											right: 8,
											top: 8,
											color: (theme) => theme.palette.grey[500],
										}}
									>
										<CloseIcon />
									</IconButton>,
									<PopoverContainer key={`dialog_popover`} markerElem={markerElem} />,
								]
							)}
						</DialogContent>
					</BootstrapDialog>
				)}
			</ClickAwayListener>
		</Box>
	)
}
